import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import apiClient from '../utils/api';
import { formatDate } from '../utils/dateFormatter';
import { stripHtmlTags } from '../utils/stringUtils';

const ArticlesListPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const location = useLocation();
  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/api/articles/');
      // Handle both array and non-array responses
      const articlesData = Array.isArray(response.data) ? response.data : [];
      setArticles(articlesData);
    } catch (error) {
      console.error('Error fetching articles:', error);
      if (error.response) {
        // Server responded with error status
        setError(`Failed to load articles: ${error.response.data?.detail || error.response.statusText || 'Server error'}`);
      } else if (error.request) {
        // Request was made but no response received (backend might be down)
        setError('Unable to connect to server. Please make sure the backend is running.');
      } else {
        // Something else happened
        setError('Failed to load articles. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // URL state management
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category') || 'all';
    const searchParam = urlParams.get('search') || '';

    setSelectedCategory(categoryParam);
    setSearchQuery(searchParam);
    setDebouncedSearchQuery(searchParam);
  }, [location.search]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);

      // Update URL with search query
      const urlParams = new URLSearchParams(location.search);
      if (searchQuery.trim()) {
        urlParams.set('search', searchQuery.trim());
      } else {
        urlParams.delete('search');
      }
      navigate({ search: urlParams.toString() }, { replace: true });
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, navigate, location.search]);

  // Update URL when category changes
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    const urlParams = new URLSearchParams(location.search);
    if (category !== 'all') {
      urlParams.set('category', category);
    } else {
      urlParams.delete('category');
    }
    navigate({ search: urlParams.toString() }, { replace: true });
  }, [navigate, location.search]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Extract unique categories from articles
  const availableCategories = useMemo(() => {
    const categoryMap = new Map();
    articles.forEach(article => {
      if (article.categories) {
        article.categories.forEach(category => {
          if (!categoryMap.has(category.id)) {
            categoryMap.set(category.id, category);
          }
        });
      }
    });
    return Array.from(categoryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [articles]);

  const processedArticles = useMemo(() => {
    return articles
      .filter(article => {
        // Filter out articles without categories
        if (!article.categories || article.categories.length === 0) return false;

        // Category filter
        if (selectedCategory && selectedCategory !== 'all') {
          const hasSelectedCategory = article.categories.some(cat =>
            cat.id.toString() === selectedCategory
          );
          if (!hasSelectedCategory) return false;
        }

        // Search filter (case-insensitive search in title, content, and meta description)
        if (debouncedSearchQuery.trim()) {
          const query = debouncedSearchQuery.toLowerCase();
          const titleMatch = article.title?.toLowerCase().includes(query);
          const contentMatch = article.content?.toLowerCase().includes(query);
          const metaMatch = article.meta_description?.toLowerCase().includes(query);
          const keywordsMatch = article.keywords?.toLowerCase().includes(query);

          if (!titleMatch && !contentMatch && !metaMatch && !keywordsMatch) return false;
        }

        return true;
      })
      .map(article => ({
        ...article,
        excerpt: article.meta_description || (article.content ? stripHtmlTags(article.content).substring(0, 150) + '...' : ''),
        categorySlug: article.categories[0].slug // Use first category's slug for URL
      }));
  }, [articles, selectedCategory, debouncedSearchQuery]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-medium text-fg mb-2">
          Articles
        </h1>
        <p className="text-lg text-fg-3">
          Explore our collection of health and nutrition articles.
        </p>
      </div>

      {/* Search Bar and Category Filter */}
      {!loading && !error && (
        <div className="mb-6 flex flex-col md:flex-row justify-between items-end">
          {/* Category Filter */}
          <div className="max-w-sm w-full">
            <label htmlFor="category-filter" className="block text-sm font-medium text-fg-2 mb-2">
              Filter by Category
            </label>
            <Select
              id="category-filter"
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full"
            >
              <option value="all">All Categories</option>
              {availableCategories.map(category => (
                <option key={category.id} value={category.id.toString()}>
                  {category.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Search Bar */}
          <div className="max-w-sm w-full">
            <label htmlFor="search-input" className="block text-sm font-medium text-fg-2 mb-2">
              Search Articles
            </label>
            <Input
              id="search-input"
              type="text"
              placeholder="Search by title, content, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      )}


      {/* Article Count */}
      {!loading && !error && (
        <div className="mb-8">
          <p className="text-sm text-fg-3">
            Showing <span className="font-medium">{processedArticles.length}</span> article{processedArticles.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <p className="text-fg-3">Loading articles...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-elev border border-line rounded-lg p-4 mb-8">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Articles List */}
      {!loading && !error && (
        <>
          {processedArticles.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                {articles.length === 0 ? (
                  <p className="text-fg-3">
                    No articles found. Check back later for new content!
                  </p>
                ) : (
                  <div className="space-y-4">
                    <p className="text-fg-3">
                      No articles match your current filters.
                    </p>
                    <p className="text-sm text-fg-3">
                      Try adjusting your search terms or category filter.
                    </p>
                    {(selectedCategory !== 'all' || debouncedSearchQuery.trim()) && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          handleCategoryChange('all');
                        }}
                        className="text-fg-2 hover:text-fg-2 dark:text-fg-2 dark:hover:text-primary-300 text-sm font-medium"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processedArticles.map((article) => (
                <Link 
                  key={article.id} 
                  to={`/${article.categorySlug}/${article.slug}`}
                  className="block"
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-300 cursor-pointer border-0">
                    {article.cover_image_url && (
                      <div className="aspect-video overflow-hidden rounded-t-lg px-6">
                        <img
                          src={article.cover_image_url}
                          alt={article.title}
                          className="w-full h-full object-cover rounded-t-lg"
                          loading="lazy"
                        />
                      </div>
                    )}
                    {/* Date right after cover image */}
                    <div className="px-6 pt-0 pb-2">
                      <p className="text-sm text-fg-3 ">
                        {formatDate(article.created_at)}
                      </p>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl text-fg line-clamp-2">
                        {article.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {article.excerpt && (
                        <p className="text-fg-3 mb-4 line-clamp-3">
                          {article.excerpt}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArticlesListPage;

